import { useMemo } from 'react';

import { PagePagination } from '@/components/pagination';
import { ProtectedComponent } from '@/components/ProtectedComponent';
import { DataTable } from '@/components/ui/data-table';

import { useEventHandler } from '@/hooks/use-event-handler';
import { useToggle } from '@/hooks/use-toggle';

import { useFetchAllPhoneNumberQuery } from '@/query/phone-number.query';

import { DEFAULT_PAGE_SIZE, Permissions } from '@/constants';

import PhoneNumberCreate from '../PhoneNumberCreate/PhoneNumberCreate';

import { getColumns } from './components/columns';
import { EmptyPhoneNumber } from './components/empty';
import { PhoneNumberHeader } from './components/phone-number-header';

import { usePhoneNumbersListStore } from '@/store/phone-numbers-list.store';
import type { PhoneNumberConfiguration } from '@/types/phone-number.type';
import type { ColumnDef } from '@tanstack/react-table';

export default function PhoneNumberListPage() {
  const { currentPage, pageSize, setCurrentPage, setPageSize, query } = usePhoneNumbersListStore();
  const { state, toggle } = useToggle(false);

  const { data, isLoading } = useFetchAllPhoneNumberQuery({
    page: currentPage,
    page_size: pageSize,
    query,
  });

  const phone_numbers: PhoneNumberConfiguration[] = data?.data || [];
  const meta = data?.meta;

  useEventHandler({
    name: 'phone-number:create',
    eventHandler: toggle as EventListener,
  });

  const columns = useMemo<ColumnDef<PhoneNumberConfiguration>[]>(
    () => getColumns(),
    [phone_numbers]
  );

  return (
    <>
      <div className="flex flex-col h-full space-y-6">
        {/** Search */}
        <PhoneNumberHeader phoneNumbers={phone_numbers} />

        <div className="bg-background border rounded-lg">
          <DataTable
            columns={columns}
            data={phone_numbers}
            isLoading={isLoading}
            emptyState={<EmptyPhoneNumber />}
          />

          {/* PagePagination */}
          <PagePagination
            isLoading={isLoading}
            currentPage={meta?.page || 1}
            pageSize={meta?.page_size || DEFAULT_PAGE_SIZE}
            totalItems={meta?.total_items || 0}
            totalPages={meta?.total_pages || 1}
            setPageSize={size => {
              setPageSize(size);
              setCurrentPage(1); // Reset to first page when page size changes
            }}
            setCurrentPage={setCurrentPage}
          />
        </div>
      </div>

      {/* Create Telephony Dialog */}
      <ProtectedComponent requiredPermissions={[Permissions.PhoneNumberCreateUpdate]}>
        {state && <PhoneNumberCreate toggle={toggle} />}
      </ProtectedComponent>
    </>
  );
}
